import { useEffect, useRef, useState, type ReactNode } from 'react'
import { painColor } from '@pelvia/shared/lib/patient/painColors'
import type { PatternMetrics } from '@pelvia/shared/hooks/patient/usePainPatterns'

/**
 * 7-day pain summary strip: average pain (with week-over-week delta),
 * pain-free days, top symptom, and the next predicted flare window. Renders
 * nothing until the pain-patterns payload is available.
 */
export default function MetricsCard({ metrics }: { metrics?: PatternMetrics }) {
  if (!metrics) return null

  return (
    <section className="card pp-metrics-card">
      <div className="pp-metrics-row">
        <Metric
          label="Pain (7-day avg)"
          numericValue={metrics.avg_pain_7d ?? null}
          decimals={1}
          fallback="—"
          painTint={metrics.avg_pain_7d != null ? metrics.avg_pain_7d : undefined}
          subRow={
            metrics.avg_pain_delta != null && (
              <span
                className={
                  metrics.avg_pain_delta > 0.2
                    ? 'pp-metric-up'
                    : metrics.avg_pain_delta < -0.2
                      ? 'pp-metric-down'
                      : 'pp-metric-neutral'
                }
              >
                {metrics.avg_pain_delta > 0 ? '↑' : metrics.avg_pain_delta < 0 ? '↓' : '→'}{' '}
                {Math.abs(metrics.avg_pain_delta)} vs prev week
              </span>
            )
          }
        />
        <Metric
          label="Pain-free days (7d)"
          numericValue={metrics.pain_free_days_7d ?? 0}
          decimals={0}
          subRow={<span className="pp-metric-neutral">out of 7</span>}
        />
        {metrics.top_symptom_30d ? (
          <Metric
            label="Top symptom"
            value={metrics.top_symptom_30d}
            subRow={
              <span className="pp-metric-neutral">
                {metrics.top_symptom_count} days in 30
              </span>
            }
          />
        ) : null}
        {metrics.next_high_pain_window ? (
          <Metric
            label="Next flare window"
            value={metrics.next_high_pain_window}
            subRow={<span className="pp-metric-neutral">from cycle pattern</span>}
          />
        ) : null}
      </div>
    </section>
  )
}

function Metric({
  label,
  value,
  numericValue,
  decimals = 0,
  fallback = '—',
  painTint,
  subRow,
}: {
  label: string
  value?: string
  numericValue?: number | null
  decimals?: number
  fallback?: string
  painTint?: number
  subRow?: ReactNode
}) {
  const isNumeric = numericValue !== undefined
  const displayValue = isNumeric
    ? numericValue == null
      ? fallback
      : ''
    : value ?? fallback

  const color = painTint != null ? painColor(painTint) : undefined

  return (
    <div className="pp-metric">
      <div className="pp-metric-label">{label}</div>
      <div
        className={`pp-metric-val${painTint != null ? ' pp-metric-val-tinted' : ''}`}
        style={color ? { color } : undefined}
      >
        {isNumeric && numericValue != null ? (
          <AnimatedNumber to={numericValue} decimals={decimals} />
        ) : (
          <FadeInValue value={displayValue} />
        )}
      </div>
      {subRow && <div className="pp-metric-sub">{subRow}</div>}
    </div>
  )
}

function AnimatedNumber({
  to,
  decimals = 0,
  duration = 650,
}: {
  to: number
  decimals?: number
  duration?: number
}) {
  const [val, setVal] = useState(0)
  const rafRef = useRef<number | null>(null)
  const fromRef = useRef(0)
  const targetRef = useRef(to)

  useEffect(() => {
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced) {
      setVal(to)
      return
    }
    const start = performance.now()
    const from = fromRef.current
    targetRef.current = to
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration)
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3)
      const next = from + (targetRef.current - from) * eased
      setVal(next)
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        fromRef.current = targetRef.current
      }
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      fromRef.current = val
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, duration])

  return <span>{val.toFixed(decimals)}</span>
}

function FadeInValue({ value }: { value: string }) {
  const [shown, setShown] = useState(false)
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true))
    return () => cancelAnimationFrame(id)
  }, [value])
  return <span className={`pp-metric-fade${shown ? ' is-shown' : ''}`}>{value}</span>
}
